/**
 * Height-aware occlusion: does a building actually stand between this canopy
 * point and the sun, and where does the point's shadow actually land?
 *
 * The 2023 model in shadow.ts asks two flat questions -- is the point inside a
 * building's ground shadow hull, and is its ground projection -- and never asks
 * how high either of them is. Put in the terms of this file, **the 2023 model
 * evaluates every canopy point as if it stood at ground level**: it tests the
 * full-height sweep `sweep(F, H)` in both directions, where the honest tests are
 * `sweep(F, H - z)` looking up and `sweep(F, min(H, z))` looking down. Two
 * consequences, both visible on screen: canopy 8 m above a roof reads as shaded
 * at noon, and a shadow cast from behind a tower passes straight through it and
 * reappears on the pavement beyond.
 *
 * Nothing here modifies that model. shadow.ts is the published work and stays
 * as it is; this is an additive second model, selected by SHADING_MODEL.
 *
 * ## How it works
 *
 * Broad phase is the existing HullIndex, unchanged. Today's full-height hulls
 * are already a conservative superset for every z -- `H - z <= H`,
 * `min(H, z) <= H`, and the convex hull over-approximates the true sweep -- so
 * no new index and no extra preprocessing are needed.
 *
 * Narrow phase clips the sun ray against each candidate's **true footprint**
 * rather than its hull, in one O(|F|) edge walk costing about what a single
 * pointInPolygon costs. That is exact in z *and* exact for concave footprints,
 * so the convex-hull artifact (5.8% false shade on a U-shaped plan) falls out of
 * the same code rather than needing separate work.
 */
import type { HullIndex } from './hullIndex';
import { FEET_PER_METRE, projectToGround, type PointClass, type SunVector } from './shadow';
import type { SelectedBuilding } from './types';

export interface OcclusionResult {
  kind: PointClass;
  /**
   * Index of the building behind the classification, or -1 for `other`. Lets
   * the caller draw only the shadows that actually produced a color.
   */
  building: number;
  /**
   * Where this point's shadow lands. On open ground that is the ground
   * projection at height 0; on a building it is the point on the roof or facade
   * the sun ray meets first.
   */
  hitLon: number;
  hitLat: number;
  hitHeight: number;
}

export function emptyResult(): OcclusionResult {
  return { kind: 'other', building: -1, hitLon: 0, hitLat: 0, hitHeight: 0 };
}

/**
 * Ground displacement per metre of height, along the sun ray.
 *
 * Taken from `projectToGround` itself rather than restated, so this model uses
 * exactly the geometry -- and exactly the empirical degree-to-metre constants --
 * that the 2023 one does. A point at height z projects to
 * `(lon + z * dLon, lat + z * dLat)`.
 */
export function sunOffsetPerMetre(sun: SunVector): [number, number] {
  return projectToGround(0, 0, 1, sun);
}

/** Below this the sun ray is vertical for all practical purposes. */
const FLAT_EPSILON = 1e-15;

export class OcclusionModel {
  private readonly footprints: Array<Array<[number, number]>>;
  /** Roof heights in metres, matching the conversion in buildingShadowHull. */
  private readonly heights: Float64Array;
  private readonly dLon: number;
  private readonly dLat: number;
  /** Sun straight overhead: the ray has no horizontal component to clip along. */
  private readonly vertical: boolean;
  /** Sun exactly on the horizon: shadows are infinite and mean nothing. */
  private readonly degenerate: boolean;

  // Scratch reused across classify() calls. This runs half a million times per
  // sun position, so the query path allocates nothing -- the same property the
  // HullIndex query path has.
  private readonly candidates: number[] = [];
  private readonly seen: Int32Array;
  private stamp = 0;
  private readonly crossings: number[] = [];
  private readonly index: HullIndex;

  constructor(buildings: SelectedBuilding[], index: HullIndex, sun: SunVector) {
    this.index = index;
    this.footprints = buildings.map((b) => b.footprint);
    this.heights = new Float64Array(buildings.length);
    for (let i = 0; i < buildings.length; i++) {
      this.heights[i] = buildings[i].heightRoofFeet / FEET_PER_METRE;
    }

    const [dLon, dLat] = sunOffsetPerMetre(sun);
    this.degenerate = !Number.isFinite(dLon) || !Number.isFinite(dLat);
    this.dLon = this.degenerate ? 0 : dLon;
    this.dLat = this.degenerate ? 0 : dLat;
    this.vertical =
      Math.abs(this.dLon) < FLAT_EPSILON && Math.abs(this.dLat) < FLAT_EPSILON;

    this.seen = new Int32Array(buildings.length);
  }

  /**
   * Classifies one canopy point, writing into `out` so the caller can reuse a
   * single result object across the whole point cloud.
   */
  classify(lon: number, lat: number, z: number, out: OcclusionResult): void {
    out.kind = 'other';
    out.building = -1;
    out.hitLon = lon + z * this.dLon;
    out.hitLat = lat + z * this.dLat;
    out.hitHeight = 0;

    // The sun on the horizon casts a shadow of infinite length in a direction
    // that is numerically meaningless, so the point stands in for its own
    // shadow rather than being drawn off the edge of the world.
    if (this.degenerate) {
      out.hitLon = lon;
      out.hitLat = lat;
      return;
    }

    // No buildings to interact with; the shadow stays on the ground where
    // projectToGround put it.
    if (this.index.size === 0) return;

    this.stamp++;
    this.candidates.length = 0;

    // Two broad-phase queries, because the two tests start from different
    // places on the same ray: a point can be shaded by the building above it
    // while its own shadow lands far outside that building's hull. Both are
    // conservative supersets of the exact test below.
    this.index.hullsAt(lon, lat, this.collect);
    this.index.hullsAt(out.hitLon, out.hitLat, this.collect);
    if (this.candidates.length === 0) return;

    // Smallest s at which the downward ray meets a building: the *first* thing
    // the shadow lands on, which is what decides where it is drawn.
    let firstHit = Infinity;
    let firstHitBuilding = -1;

    for (const c of this.candidates) {
      const height = this.heights[c];
      const footprint = this.footprints[c];

      if (this.vertical) {
        // Degenerate but worth keeping honest: with the sun overhead the ray is
        // the vertical line through the point, inside the footprint or not.
        if (!containsPoint(footprint, lon, lat)) continue;
        if (z < height) {
          out.kind = 'shaded';
          out.building = c;
        } else if (height < firstHit) {
          firstHit = z - height;
          firstHitBuilding = c;
        }
        continue;
      }

      const n = this.rayCrossings(lon, lat, footprint);
      if (n === 0) continue;

      // Looking up toward the sun: s runs from 0 at the point to -(H - z) at
      // roof height. Anything solid in that span occludes the sun.
      if (z < height) {
        const lo = z - height;
        for (let k = 0; k + 1 < n; k += 2) {
          if (this.crossings[k] <= 0 && this.crossings[k + 1] >= lo) {
            out.kind = 'shaded';
            out.building = c;
            break;
          }
        }
      }

      // Looking down along the shadow: s runs from 0 at the point to z at the
      // ground. The ray cannot hit this building above its roof, so it cannot
      // hit before s = z - H. Clamping there rather than letting the ray run to
      // s = z is what stops shadows passing through buildings.
      const from = z > height ? z - height : 0;
      for (let k = 0; k + 1 < n; k += 2) {
        const a = this.crossings[k];
        const b = this.crossings[k + 1];
        if (b < from || a > z) continue;
        const s = a > from ? a : from;
        if (s < firstHit) {
          firstHit = s;
          firstHitBuilding = c;
        }
      }
    }

    if (firstHitBuilding >= 0) {
      out.hitLon = lon + firstHit * this.dLon;
      out.hitLat = lat + firstHit * this.dLat;
      out.hitHeight = z - firstHit;
      // A point in shade casts no shadow worth counting, so `shaded` wins --
      // the same precedence classifyPoint applies. The hit position is still
      // written, so the shadow layer does not draw it through a wall.
      if (out.kind !== 'shaded') {
        out.kind = 'shading';
        out.building = firstHitBuilding;
      }
    }
  }

  /** Bound once: passing an arrow function per point would allocate per point. */
  private readonly collect = (i: number): void => {
    if (this.seen[i] === this.stamp) return;
    this.seen[i] = this.stamp;
    this.candidates.push(i);
  };

  /**
   * Where the sun ray through (lon, lat) crosses a footprint's boundary.
   *
   * The ray is parameterised in metres of height: at parameter `s` the ray's
   * plan position is `(lon + s * dLon, lat + s * dLat)` and its height is
   * `z - s`. So `s > 0` walks the shadow downward and `s < 0` walks toward the
   * sun, and the same crossing set answers both tests -- the canopy point and
   * its ground projection lie on one line.
   *
   * Crossings are written to `this.crossings` in ascending order; the ray is
   * inside the footprint between the first and second, the third and fourth,
   * and so on. That pairing is what makes concave plans exact: a U-shaped
   * building yields two intervals with a genuine gap between them, which a
   * convex hull cannot express.
   *
   * This is `pointInPolygon`'s ray cast generalised to an arbitrary direction,
   * keeping its half-open comparison so vertices on the line are counted once.
   */
  private rayCrossings(lon: number, lat: number, footprint: Array<[number, number]>): number {
    const { dLon, dLat, crossings } = this;
    // Recover s from whichever component carries more of the direction; near a
    // north-south or east-west sun the other one is nearly zero.
    const useLon = Math.abs(dLon) >= Math.abs(dLat);
    let n = 0;

    for (let i = 0, j = footprint.length - 1; i < footprint.length; j = i++) {
      const [ax, ay] = footprint[j];
      const [bx, by] = footprint[i];

      // Signed offset from the ray's line, positive on one side of it.
      const wa = (ay - lat) * dLon - (ax - lon) * dLat;
      const wb = (by - lat) * dLon - (bx - lon) * dLat;
      if (wa > 0 === wb > 0) continue;

      const t = wa / (wa - wb);
      const s = useLon
        ? (ax + t * (bx - ax) - lon) / dLon
        : (ay + t * (by - ay) - lat) / dLat;

      // Insertion sort: a footprint crosses the line twice, four times on a
      // U-plan, and essentially never more.
      let k = n++;
      while (k > 0 && crossings[k - 1] > s) {
        crossings[k] = crossings[k - 1];
        k--;
      }
      crossings[k] = s;
    }

    // An odd count means the line grazed the boundary in a way the half-open
    // rule could not resolve. Pairing those would invent an interval running to
    // infinity, so the footprint is skipped instead.
    return n % 2 === 0 ? n : 0;
  }
}

/** Ray-casting containment, inlined to avoid an array per test. */
function containsPoint(vs: Array<[number, number]>, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const [xi, yi] = vs[i];
    const [xj, yj] = vs[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
